import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Music2, RefreshCw, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config';
import { runAiJob } from '../utils/aiJobs';

const API = API_URL;

/**
 * NextTrackSuggester — Sugeridor IA en vivo durante el evento.
 *
 * Botón flotante (FAB) inferior derecho. Al pulsar abre modal con 3 sugerencias
 * generadas por GPT-5.2 basadas en:
 *   - Canción actual (BPM, género)
 *   - Fase del set (calentamiento/peak/descenso/cierre)
 *   - Historial completo (no repite pinchadas)
 *   - Imprescindibles pendientes
 *   - Feedback negativo previo del tipo de evento
 *
 * Props:
 *   - eventId: ID de la playlist/evento
 *   - currentSongId: ID de la canción actualmente sonando (opcional)
 *   - token: JWT
 *   - visible: boolean (solo se muestra en isLiveMode)
 */
export const NextTrackSuggester = ({ eventId, currentSongId, token, visible = true }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [contextSummary, setContextSummary] = useState(null);
  const [logId, setLogId] = useState(null);
  const [excludeIds, setExcludeIds] = useState([]); // para "Otra sugerencia"
  const [errorMsg, setErrorMsg] = useState('');

  const fetchSuggestions = async (additionalExcludes = []) => {
    if (!eventId) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const allExcludes = [...excludeIds, ...additionalExcludes];
      const { data } = await runAiJob({
        startUrl: `${API}/dj/next-track-suggest/start`,
        statusBase: `${API}/dj/jobs`,
        body: {
          event_id: eventId,
          current_song_id: currentSongId || null,
          exclude_ids: allExcludes,
          limit: 3,
        },
        config: { headers: { Authorization: `Bearer ${token}` } },
      });

      if (data.success) {
        setSuggestions(data.suggestions || []);
        setContextSummary(data.context_summary);
        setLogId(data.suggestion_log_id);
        setExcludeIds(allExcludes);
      } else {
        setSuggestions([]);
        setContextSummary(data.context_summary);
        setErrorMsg(data.message || 'No hay candidatas disponibles ahora mismo.');
      }
    } catch (err) {
      console.error('[NextTrackSuggester] error', err);
      const msg = err?.response?.data?.detail || 'Error al obtener sugerencias';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSuggestions([]);
    setExcludeIds([]);
    setLogId(null);
    setErrorMsg('');
    fetchSuggestions();
  };

  const handleAccept = async (songId, title, artist) => {
    if (logId) {
      try {
        await axios.post(
          `${API}/dj/next-track-accept`,
          { suggestion_log_id: logId, accepted_song_id: songId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        // No-bloqueante, solo log
        console.warn('[NextTrackSuggester] accept log failed', e);
      }
    }
    toast.success(`Próxima: ${title} — ${artist}`, { duration: 4000 });
    setOpen(false);
  };

  const handleOtherSuggestion = () => {
    // Excluir las 3 ya mostradas y volver a pedir
    const newExcludes = suggestions.map((s) => s.song_id);
    fetchSuggestions(newExcludes);
  };

  const handleRejectAll = async () => {
    if (logId) {
      try {
        await axios.post(
          `${API}/dj/next-track-accept`,
          { suggestion_log_id: logId, accepted_song_id: null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) { /* noop */ }
    }
    setOpen(false);
  };

  // Iconos medalla por posición
  const getMedal = (idx) => ['🥇', '🥈', '🥉'][idx] || '🎵';

  // Color por fase
  const faseColor = {
    calentamiento: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    peak: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
    descenso: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    cierre: 'bg-pink-500/20 text-pink-300 border-pink-500/40',
  };

  if (!visible) return null;

  return (
    <>
      {/* FAB — botón flotante */}
      <button
        onClick={handleOpen}
        className="fixed bottom-24 right-4 z-50 bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
        data-testid="next-track-suggester-fab"
        title="Sugerir siguiente canción (IA)"
        aria-label="Sugerir siguiente canción"
      >
        <Sparkles className="w-7 h-7" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl" data-testid="next-track-suggester-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              Siguiente canción · sugerencia IA
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              GPT-5.2 analiza la fase del set, BPM y el historial del evento para proponer las 3 mejores opciones.
            </DialogDescription>
          </DialogHeader>

          {/* Contexto del set */}
          {contextSummary && (
            <div className="flex flex-wrap gap-2 items-center mb-2">
              <Badge variant="outline" className={faseColor[contextSummary.fase] || ''}>
                Fase: {contextSummary.fase}
              </Badge>
              <Badge variant="outline" className="bg-slate-800 text-slate-300 border-slate-600">
                {contextSummary.position} ({contextSummary.progreso_pct}%)
              </Badge>
              {contextSummary.current_song?.title && (
                <Badge variant="outline" className="bg-slate-800 text-slate-300 border-slate-600 max-w-full">
                  <Music2 className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    Ahora: {contextSummary.current_song.title} — {contextSummary.current_song.artist}
                    {contextSummary.current_song.bpm ? ` · ${contextSummary.current_song.bpm} BPM` : ''}
                  </span>
                </Badge>
              )}
              {contextSummary.filter_used && contextSummary.filter_used !== 'estricto' && (
                <Badge variant="outline" className="bg-amber-900/30 text-amber-200 border-amber-700/50 text-[10px]">
                  Filtro relajado: {contextSummary.filter_used}
                </Badge>
              )}
            </div>
          )}

          {/* Estado: cargando */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
              <p className="text-slate-400 text-sm">Analizando contexto y consultando IA…</p>
            </div>
          )}

          {/* Estado: error o sin candidatas */}
          {!loading && errorMsg && (
            <div className="bg-amber-900/20 border border-amber-700/40 rounded p-4 text-amber-200 text-sm">
              {errorMsg}
            </div>
          )}

          {/* Sugerencias */}
          {!loading && suggestions.length > 0 && (
            <div className="space-y-2">
              {suggestions.map((sug, idx) => (
                <Card
                  key={sug.song_id}
                  className="bg-slate-800/60 border-slate-700 hover:border-cyan-500/50 transition-colors"
                  data-testid={`next-track-suggestion-${idx}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0" aria-hidden="true">{getMedal(idx)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{sug.title}</p>
                        <p className="text-slate-400 text-sm truncate">{sug.artist}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {sug.bpm && (
                            <Badge variant="outline" className="text-xs bg-slate-900 text-slate-300 border-slate-600">
                              {sug.bpm} BPM
                            </Badge>
                          )}
                          {sug.energia && (
                            <Badge variant="outline" className="text-xs bg-slate-900 text-slate-300 border-slate-600">
                              {sug.energia}
                            </Badge>
                          )}
                          {(sug.genres || []).slice(0, 2).map((g) => (
                            <Badge key={g} variant="outline" className="text-xs bg-slate-900 text-cyan-300 border-cyan-700/40">
                              {g}
                            </Badge>
                          ))}
                        </div>
                        {sug.reason && (
                          <p className="text-cyan-300/80 text-xs mt-2 italic">💡 {sug.reason}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAccept(sug.song_id, sug.title, sug.artist)}
                        className="bg-cyan-600 hover:bg-cyan-500 text-white flex-shrink-0"
                        data-testid={`next-track-accept-${idx}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Esta
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Footer: otras acciones */}
          {!loading && suggestions.length > 0 && (
            <div className="flex justify-between items-center mt-2 gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRejectAll}
                className="text-slate-400 hover:text-slate-200"
                data-testid="next-track-reject-all"
              >
                <X className="w-4 h-4 mr-1" />
                Ninguna encaja
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOtherSuggestion}
                disabled={loading}
                className="bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                data-testid="next-track-other-suggestion"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Otra sugerencia
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NextTrackSuggester;
